import {
  Button,
  Flex,
  Grid,
  SelectField,
  TextAreaField,
  TextField,
  CheckboxField,
} from "@aws-amplify/ui-react";
import { useEffect, useState } from "react";
import LocalizedStrings from "react-localization";

const FAKE_FARMS = [
  { id: "farm-001", name: "Finca Las Palmas" },
  { id: "farm-002", name: "Finca El Roble" },
  { id: "farm-003", name: "Finca La Esperanza" },
  { id: "farm-004", name: "Finca San José" },
  { id: "farm-005", name: "Finca Santa Rita" },
];

const FAKE_LOTS_BY_FARM = {
  "farm-001": [
    { id: "lot-001A", name: "Lote A" },
    { id: "lot-001B", name: "Lote B" },
    { id: "lot-001C", name: "Lote C" },
    { id: "lot-001D", name: "Lote D" },
    { id: "lot-001E", name: "Lote E" },
  ],
  "farm-002": [
    { id: "lot-002A", name: "Lote A" },
    { id: "lot-002B", name: "Lote B" },
    { id: "lot-002C", name: "Lote C" },
    { id: "lot-002D", name: "Lote D" },
    { id: "lot-002E", name: "Lote E" },
  ],
};

const FAKE_CROP_UNITS_BY_LOT = {
  "lot-001A": [
    { id: "cu-001", name: "Unidad 001" },
    { id: "cu-002", name: "Unidad 002" },
    { id: "cu-003", name: "Unidad 003" },
    { id: "cu-004", name: "Unidad 004" },
    { id: "cu-005", name: "Unidad 005" },
  ],
  "lot-002A": [
    { id: "cu-101", name: "Unidad 101" },
    { id: "cu-102", name: "Unidad 102" },
    { id: "cu-103", name: "Unidad 103" },
    { id: "cu-104", name: "Unidad 104" },
    { id: "cu-105", name: "Unidad 105" },
  ],
};

const FAKE_COLLABORATORS = [
  { id: "col-0001", name: "Juan Pérez" },
  { id: "col-0002", name: "María Gómez" },
  { id: "col-0003", name: "Carlos Rojas" },
  { id: "col-0004", name: "Ana Martínez" },
  { id: "col-0005", name: "Luis Díaz" },
];

const FAKE_WAREHOUSES_BY_FARM = {
  "farm-001": [
    { id: "wh-001", name: "Almacén Central" },
    { id: "wh-002", name: "Almacén Norte" },
    { id: "wh-003", name: "Almacén Sur" },
    { id: "wh-004", name: "Almacén Este" },
    { id: "wh-005", name: "Almacén Oeste" },
  ],
};

const UNIT_TYPES = ["", "KG"];

const HarvestForm = ({
  onSubmit,
  type = "create",
  initialValues = {},
  onCancel,
}) => {
  const defaultState = {
    date: "",
    farmId: "",
    farmName: "",
    lotId: "",
    lotName: "",
    cropUnitId: "",
    cropUnitName: "",
    responsiblePersonId: "",
    responsiblePersonName: "",
    isContractor: false,
    contractorData: { name: "", taxId: "", invoice: "" },
    unitType: "KG",
    quantity: "",
    postHarvestWarehouseId: "",
    postHarvestWarehouseName: "",
    photoEvidence: "",
    additionalObservations: "",
    createdAt: "",
  };

  const [form, setForm] = useState({ ...defaultState, ...initialValues });
  const [availableLots, setAvailableLots] = useState([]);
  const [availableCropUnits, setAvailableCropUnits] = useState([]);
  const [availableWarehouses, setAvailableWarehouses] = useState([]);

  useEffect(() => {
    setForm({ ...defaultState, ...initialValues });

    if (initialValues.farmId) {
      const lots = FAKE_LOTS_BY_FARM[initialValues.farmId] || [];
      setAvailableLots(lots);
      const whs = FAKE_WAREHOUSES_BY_FARM[initialValues.farmId] || [];
      setAvailableWarehouses(whs);
      if (initialValues.lotId) {
        const cus = FAKE_CROP_UNITS_BY_LOT[initialValues.lotId] || [];
        setAvailableCropUnits(cus);
      }
    }

    if (
      initialValues.responsiblePersonId &&
      !initialValues.responsiblePersonName
    ) {
      const found = FAKE_COLLABORATORS.find(
        (c) => c.id === initialValues.responsiblePersonId
      );
      if (found) setForm((f) => ({ ...f, responsiblePersonName: found.name }));
    }

    // if contractorData is string, try parse (not required)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const handleFarmChange = (farmId) => {
    const farm = FAKE_FARMS.find((f) => f.id === farmId);
    const lots = FAKE_LOTS_BY_FARM[farmId] || [];
    const whs = FAKE_WAREHOUSES_BY_FARM[farmId] || [];
    setAvailableLots(lots);
    setAvailableWarehouses(whs);
    setAvailableCropUnits([]);
    setForm({
      ...form,
      farmId,
      farmName: farm ? farm.name : "",
      lotId: "",
      lotName: "",
      cropUnitId: "",
      cropUnitName: "",
      postHarvestWarehouseId: "",
      postHarvestWarehouseName: "",
    });
  };

  const handleLotChange = (lotId) => {
    const lot = (availableLots || []).find((l) => l.id === lotId);
    const cus = FAKE_CROP_UNITS_BY_LOT[lotId] || [];
    setAvailableCropUnits(cus);
    setForm({
      ...form,
      lotId,
      lotName: lot ? lot.name : "",
      cropUnitId: "",
      cropUnitName: "",
    });
  };

  const handleCropUnitChange = (cropUnitId) => {
    const cu = (availableCropUnits || []).find((c) => c.id === cropUnitId);
    setForm({ ...form, cropUnitId, cropUnitName: cu ? cu.name : "" });
  };

  const handleResponsibleChange = (id) => {
    const found = FAKE_COLLABORATORS.find((c) => c.id === id);
    setForm({
      ...form,
      responsiblePersonId: id,
      responsiblePersonName: found ? found.name : "",
    });
  };

  const handleWarehouseChange = (warehouseId) => {
    const wh = (availableWarehouses || []).find((w) => w.id === warehouseId);
    setForm({
      ...form,
      postHarvestWarehouseId: warehouseId,
      postHarvestWarehouseName: wh ? wh.name : "",
    });
  };

  const onSubmitForm = () => {
    const payload = { ...form };
    if (!payload.createdAt) payload.createdAt = new Date().toISOString();
    onSubmit && onSubmit(payload);
  };

  return (
    <Grid rowGap="12px" columnGap="12px" padding="20px">
      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <TextField
          name="date"
          label={strings.fields.date.label}
          placeholder="YYYY-MM-DD"
          value={form.date || ""}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
        />
        <SelectField
          name="farmId"
          label={strings.fields.farmId.label}
          value={form.farmId || ""}
          onChange={(e) => handleFarmChange(e.target.value)}
        >
          <option value="">--</option>
          {FAKE_FARMS.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </SelectField>
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <SelectField
          name="lotId"
          label={strings.fields.lotId.label}
          value={form.lotId || ""}
          onChange={(e) => handleLotChange(e.target.value)}
        >
          <option value="">--</option>
          {availableLots.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </SelectField>

        <SelectField
          name="cropUnitId"
          label={strings.fields.cropUnitId.label}
          value={form.cropUnitId || ""}
          onChange={(e) => handleCropUnitChange(e.target.value)}
        >
          <option value="">--</option>
          {availableCropUnits.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </SelectField>
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <SelectField
          name="responsiblePersonId"
          label={strings.fields.responsiblePersonId.label}
          value={form.responsiblePersonId || ""}
          onChange={(e) => handleResponsibleChange(e.target.value)}
        >
          <option value="">--</option>
          {FAKE_COLLABORATORS.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </SelectField>

        <CheckboxField
          name="isContractor"
          label={strings.fields.isContractor.label}
          isChecked={!!form.isContractor}
          onChange={(e) => setForm({ ...form, isContractor: e.target.checked })}
        />
      </Grid>

      {form.isContractor && (
        <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
          <TextField
            name="contractorName"
            label={strings.fields.contractorName.label}
            value={form.contractorData?.name || ""}
            onChange={(e) =>
              setForm({
                ...form,
                contractorData: {
                  ...form.contractorData,
                  name: e.target.value,
                },
              })
            }
          />
          <TextField
            name="contractorTaxId"
            label={strings.fields.contractorTaxId.label}
            value={form.contractorData?.taxId || ""}
            onChange={(e) =>
              setForm({
                ...form,
                contractorData: {
                  ...form.contractorData,
                  taxId: e.target.value,
                },
              })
            }
          />
          <TextField
            name="contractorInvoice"
            label={strings.fields.contractorInvoice.label}
            value={form.contractorData?.invoice || ""}
            onChange={(e) =>
              setForm({
                ...form,
                contractorData: {
                  ...form.contractorData,
                  invoice: e.target.value,
                },
              })
            }
          />
        </Grid>
      )}

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <SelectField
          name="unitType"
          label={strings.fields.unitType.label}
          value={form.unitType || ""}
          onChange={(e) => setForm({ ...form, unitType: e.target.value })}
        >
          {UNIT_TYPES.map((u) => (
            <option key={u} value={u}>
              {u || "--"}
            </option>
          ))}
        </SelectField>

        <TextField
          name="quantity"
          label={strings.fields.quantity.label}
          placeholder="e.g. 1000"
          value={form.quantity || ""}
          onChange={(e) => setForm({ ...form, quantity: e.target.value })}
        />
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <SelectField
          name="postHarvestWarehouseId"
          label={strings.fields.postHarvestWarehouseId.label}
          value={form.postHarvestWarehouseId || ""}
          onChange={(e) => handleWarehouseChange(e.target.value)}
        >
          <option value="">--</option>
          {availableWarehouses.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </SelectField>

        <TextField
          name="photoEvidence"
          label={strings.fields.photoEvidence.label}
          placeholder="foto1.jpg, foto2.jpg"
          value={
            Array.isArray(form.photoEvidence)
              ? form.photoEvidence.join(", ")
              : form.photoEvidence || ""
          }
          onChange={(e) => setForm({ ...form, photoEvidence: e.target.value })}
        />
      </Grid>

      <TextAreaField
        name="additionalObservations"
        label={strings.fields.additionalObservations.label}
        value={form.additionalObservations || ""}
        onChange={(e) =>
          setForm({ ...form, additionalObservations: e.target.value })
        }
      />

      <Flex justifyContent="end">
        <Button
          children={strings.fields.buttons.reset}
          onClick={() => setForm(defaultState)}
        />
        <Button
          children={
            type === "create"
              ? strings.fields.buttons.create
              : strings.fields.buttons.save
          }
          onClick={onSubmitForm}
          variation="primary"
        />
      </Flex>
    </Grid>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      date: { label: "Fecha" },
      farmId: { label: "Finca" },
      lotId: { label: "Lote" },
      cropUnitId: { label: "Unidad de cultivo" },
      responsiblePersonId: { label: "Persona responsable" },
      isContractor: { label: "¿Realizado por contratista?" },
      contractorName: { label: "Nombre contratista" },
      contractorTaxId: { label: "NIT contratista" },
      contractorInvoice: { label: "Factura contratista (archivo)" },
      unitType: { label: "Unidad" },
      quantity: { label: "Cantidad" },
      postHarvestWarehouseId: { label: "Almacén pos cosecha" },
      photoEvidence: { label: "Evidencia fotográfica" },
      additionalObservations: { label: "Observaciones adicionales" },
      createdAt: { label: "Fecha de creación" },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
        cancel: "Cancelar",
      },
    },
  },
});

export default HarvestForm;
